import { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Divider,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GroupWorkOutlined, TuneOutlined } from "@mui/icons-material";
import { MetricInfo, GUIDES_CENTRAL } from "../../metricInfo";
import useStyles from "./styles";

// Aba "Parâmetros" da tela de config: ajusta a janela de correlação
// alerta -> atendimento (minutos após a queda em que um atendimento do mesmo
// serviço conta como resposta ao alerta) e a janela de AGRUPAMENTO de
// incidentes (cluster: disparos da mesma trigger no mesmo equipamento dentro
// da janela viram um incidente só; 0 = desligado). O valor da correlação
// também é o limiar da zona morta — as regras andam juntas no backend
// (ver correlacaoIncidentes.js).
//
// Os dois parâmetros compartilham UM rodapé de salvamento: o POST aceita os
// dois campos no mesmo payload, então não existe mais o reenvio da correlação
// vigente que o card do agrupamento fazia só para satisfazer a validação.

const JANELA_RECOMENDADA_MINUTOS = 30;

// Estado de um campo numérico: validade, se mudou em relação ao salvo e se o
// texto digitado difere do salvo (o "difere" também vale para valor inválido,
// que é o que destrava o botão Descartar).
function estadoCampo(texto, salvo, min, max) {
	const numero = Number(texto);
	const valido =
		texto !== "" &&
		Number.isInteger(numero) &&
		numero >= min &&
		numero <= max;
	return {
		numero,
		valido,
		invalido: texto !== "" && !valido,
		vazio: texto === "",
		alterado: valido && numero !== Number(salvo),
		diferente: texto !== String(salvo ?? ""),
	};
}

const rotuloMinutos = (n, zeroDesligado) =>
	zeroDesligado && Number(n) === 0 ? "desligado" : `${n} min`;

export default function AbaParametros({ parametros, onSalvar }) {
	const theme = useTheme();
	const styles = useStyles(theme);

	const [valor, setValor] = useState("");
	const [valorAgrupamento, setValorAgrupamento] = useState("");
	const [salvando, setSalvando] = useState(false);

	useEffect(() => {
		if (parametros?.janelaCorrelacaoMinutos != null) {
			setValor(String(parametros.janelaCorrelacaoMinutos));
		}
		if (parametros?.janelaAgrupamentoMinutos != null) {
			setValorAgrupamento(String(parametros.janelaAgrupamentoMinutos));
		}
	}, [parametros]);

	const minimo = parametros?.limites?.min ?? 1;
	const maximo = parametros?.limites?.max ?? 1440;
	const minimoAgrupamento = parametros?.limitesAgrupamento?.min ?? 0;
	const maximoAgrupamento = parametros?.limitesAgrupamento?.max ?? 1440;

	const correlacao = estadoCampo(
		valor,
		parametros?.janelaCorrelacaoMinutos,
		minimo,
		maximo
	);
	const agrupamento = estadoCampo(
		valorAgrupamento,
		parametros?.janelaAgrupamentoMinutos,
		minimoAgrupamento,
		maximoAgrupamento
	);

	// Rodapé: lista de pendências e de erros, para o operador saber o que falta
	// antes de salvar.
	const invalidos = [];
	if (correlacao.invalido || correlacao.vazio) invalidos.push("janela de correlação");
	if (agrupamento.invalido || agrupamento.vazio) invalidos.push("agrupamento");

	const mudancas = [];
	if (correlacao.alterado) {
		mudancas.push(
			`correlação ${parametros?.janelaCorrelacaoMinutos} → ${correlacao.numero} min`
		);
	}
	if (agrupamento.alterado) {
		mudancas.push(
			`agrupamento ${rotuloMinutos(parametros?.janelaAgrupamentoMinutos, true)} → ${rotuloMinutos(agrupamento.numero, true)}`
		);
	}

	const temErro = invalidos.length > 0;
	const podeSalvar = mudancas.length > 0 && !temErro && !salvando;
	// Descartar também serve para sair de um valor inválido ou vazio.
	const podeDescartar =
		(correlacao.diferente || agrupamento.diferente) && !salvando;

	const descartar = () => {
		setValor(String(parametros?.janelaCorrelacaoMinutos ?? ""));
		setValorAgrupamento(String(parametros?.janelaAgrupamentoMinutos ?? ""));
	};

	const salvar = async () => {
		setSalvando(true);
		try {
			await onSalvar({
				janelaCorrelacaoMinutos: correlacao.numero,
				janelaAgrupamentoMinutos: agrupamento.numero,
			});
		} finally {
			setSalvando(false);
		}
	};

	const linha = ({ icone, nome, legenda, extra, guia, campo }) => (
		<Box sx={styles.linha}>
			<Box sx={styles.conteudo}>
				{icone}
				<Box sx={styles.textos}>
					<Typography sx={styles.nome}>{nome}</Typography>
					<Typography variant="caption" sx={styles.legenda}>
						{legenda}
					</Typography>
					<Box sx={styles.linhaVigente}>
						<Typography variant="caption" sx={styles.rotuloVigente}>
							Em vigor:
						</Typography>
						<Typography variant="caption" sx={styles.valorVigente}>
							{extra.valor}
						</Typography>
						<Typography variant="caption" sx={styles.rotuloVigente}>
							{extra.observacao}
						</Typography>
					</Box>
				</Box>
			</Box>
			<Box sx={styles.controles}>
				<Box sx={styles.espacoInfo}>
					<MetricInfo theme={theme} guide={guia} />
				</Box>
				{campo}
			</Box>
		</Box>
	);

	return (
		<Box sx={styles.pilha}>
			<Paper
				sx={styles.container}
				variant={theme.palette.mode === "dark" ? "outlined" : "elevation"}
				elevation={theme.palette.mode === "dark" ? 0 : 3}
			>
				<Typography variant="mySubtitle">Parâmetros de correlação</Typography>
				<Typography variant="body2" sx={styles.intro}>
					Como a Central liga alerta, atendimento e ordem de serviço. Salvar
					invalida o cache dos motores na hora — o painel passa a usar os novos
					valores no próximo carregamento.
				</Typography>
				<Divider sx={styles.divisorTopo} />

				<Stack divider={<Divider flexItem />} spacing={0}>
					<Box>
						{linha({
							icone: <TuneOutlined sx={styles.iconeParametro} />,
							nome: "Janela de correlação alerta → atendimento",
							legenda:
								"Um atendimento do mesmo serviço aberto até N minutos após a queda conta como resposta ao alerta",
							extra: {
								valor: rotuloMinutos(parametros?.janelaCorrelacaoMinutos, false),
								observacao: "· também é o limiar da zona morta",
							},
							guia: GUIDES_CENTRAL.parametrosJanela,
							campo: (
								<TextField
									size="small"
									type="number"
									label="Janela (minutos)"
									value={valor}
									onChange={(evento) => setValor(evento.target.value)}
									inputProps={{ min: minimo, max: maximo, step: 1 }}
									error={correlacao.invalido}
									helperText={
										correlacao.invalido
											? `Use um inteiro entre ${minimo} e ${maximo}`
											: `Recomendado: ${JANELA_RECOMENDADA_MINUTOS} min`
									}
									sx={styles.campo}
								/>
							),
						})}
						{correlacao.valido &&
							correlacao.numero > JANELA_RECOMENDADA_MINUTOS && (
								<Alert severity="warning" variant="outlined" sx={styles.aviso}>
									Janelas maiores que {JANELA_RECOMENDADA_MINUTOS} min podem
									casar atendimento com alerta sem relação causal, inflando o
									MTTA e atrasando a detecção de zona morta.
								</Alert>
							)}
					</Box>

					{linha({
						icone: <GroupWorkOutlined sx={styles.iconeParametro} />,
						nome: "Agrupamento de incidentes",
						legenda:
							"Disparos da mesma trigger no mesmo equipamento dentro da janela são exibidos como um único incidente",
						extra: {
							valor: rotuloMinutos(parametros?.janelaAgrupamentoMinutos, true),
							observacao: "· os KPIs e o gráfico seguem contando alertas crus",
						},
						guia: GUIDES_CENTRAL.parametrosAgrupamento,
						campo: (
							<TextField
								size="small"
								type="number"
								label="Agrupamento (minutos)"
								value={valorAgrupamento}
								onChange={(evento) => setValorAgrupamento(evento.target.value)}
								inputProps={{
									min: minimoAgrupamento,
									max: maximoAgrupamento,
									step: 1,
								}}
								error={agrupamento.invalido}
								helperText={
									agrupamento.invalido
										? `Use um inteiro entre ${minimoAgrupamento} e ${maximoAgrupamento}`
										: "0 = agrupamento desligado"
								}
								sx={styles.campo}
							/>
						),
					})}
				</Stack>

				{parametros && !parametros.persistido && (
					<Typography variant="caption" sx={styles.observacao}>
						{`Nenhum valor salvo ainda — está valendo o padrão do ambiente (${parametros.padraoAmbiente} min).`}
					</Typography>
				)}
			</Paper>

			<Paper
				sx={styles.rodape}
				variant={theme.palette.mode === "dark" ? "outlined" : "elevation"}
				elevation={theme.palette.mode === "dark" ? 0 : 3}
			>
				<Box sx={styles.estadoRodape}>
					<Box
						sx={styles.pontoEstado(
							temErro
								? theme.palette.error.main
								: mudancas.length
									? theme.palette.warning.main
									: theme.palette.divider
						)}
					/>
					<Box sx={styles.textosRodape}>
						<Typography variant="body2" sx={styles.tituloRodape}>
							{temErro
								? `Corrija o valor de ${invalidos.join(" e ")}`
								: mudancas.length === 0
									? "Nenhuma alteração pendente"
									: mudancas.length === 1
										? "1 alteração não salva"
										: `${mudancas.length} alterações não salvas`}
						</Typography>
						<Typography variant="caption" sx={styles.detalheRodape}>
							{mudancas.length
								? mudancas.join("  ·  ")
								: temErro
									? "Use um número inteiro de minutos"
									: `Em vigor: correlação ${rotuloMinutos(parametros?.janelaCorrelacaoMinutos, false)} · agrupamento ${rotuloMinutos(parametros?.janelaAgrupamentoMinutos, true)}`}
						</Typography>
					</Box>
				</Box>

				<Box sx={styles.acoesRodape}>
					<Button size="small" onClick={descartar} disabled={!podeDescartar}>
						Descartar
					</Button>
					<Button
						variant="contained"
						size="small"
						onClick={salvar}
						disabled={!podeSalvar}
					>
						{salvando ? "Salvando…" : "Salvar alterações"}
					</Button>
				</Box>
			</Paper>
		</Box>
	);
}
